import React from "react";
import { Label, Divider, Grid, Header, Input } from "semantic-ui-react";
import { get, pullAllBy, flatten, zip, isEmpty } from "lodash";

import AppLink from "../Common/AppLink";
import DeleteModal from "../Common/DeleteModal";

const highLightedSearchMatch = (filter, source) => {
  if (!source) {
    return;
  } else {
    source = source.toString();
  }
  const regex = new RegExp(filter, "gi");
  const occurrences = source.match(regex);
  if (!occurrences || !filter) return source;

  const splitByOccurrences = source.split(regex);
  const highlightedSource = flatten(
    pullAllBy(flatten(zip(splitByOccurrences, occurrences)), element => element.length > 0).map((element, index) =>
      element === occurrences[0] ? <b key={index}>{occurrences[0]}</b> : element
    )
  );
  return highlightedSource;
};

export const TemplateList = ({ templates, filter, onFilter }) => {
  const featureNames = !isEmpty(templates) ? getFeatureNames(templates) : [];
  const bundleNames = !isEmpty(templates) ? getBundleNames(templates) : [];
  const licenseBundleNames = !isEmpty(templates) ? getLicenseBundleNames(templates) : [];
  return (
    <Grid padded>
      <Grid.Row verticalAlign="middle">
        <Grid.Column textAlign="left" width={2}>
          <h3>TEMPLATES</h3>
        </Grid.Column>
        <Grid.Column>
          <Label size="large" circular color="black">
            {get(templates, "length", 0)}
          </Label>
        </Grid.Column>

        <Grid.Column width={5}>
          <Input
            icon="search"
            autoFocus={true}
            data-test="templateNameSearchInput"
            fluid
            placeholder="Search by template, feature or bundle name"
            value={filter.name}
            onChange={(e, data) => {
              onFilter({ ...filter, name: data.value });
            }}
          />
        </Grid.Column>
        <Grid.Column width={5} />
        <Grid.Column width={3} textAlign="right">
          <AppLink to="/templates/new" icon="add" background content="New Template" />
        </Grid.Column>
      </Grid.Row>
      <Grid.Row>
        <Grid.Column width={3} as={Header} size="small">
          Name
        </Grid.Column>
        <Grid.Column width={4} as={Header} size="small">
          Features
        </Grid.Column>
        <Grid.Column width={4} as={Header} size="small">
          Bundles
        </Grid.Column>
        <Grid.Column width={3} as={Header} size="small">
          License Bundles
        </Grid.Column>
        <Grid.Column textAlign="center" width={2} as={Header} size="small">
          Actions
        </Grid.Column>
      </Grid.Row>
      <Divider />
      {!isEmpty(templates) &&
        templates.map((template, index) => (
          <TemplateRow
            row={index}
            template={template}
            filter={filter}
            featureNames={featureNames}
            bundleNames={bundleNames}
            licenseBundleNames={licenseBundleNames}
            key={index}
          />
        ))}

      {isEmpty(templates) && (
        <Grid.Row textAlign="center">
          <Grid.Column>No templates</Grid.Column>
        </Grid.Row>
      )}
    </Grid>
  );
};

const getFeatureNames = templates =>
  Object.fromEntries(
    templates.map(template => [
      template.id,
      get(template, "templatesFeatures", [])
        .map(rel => rel.feature.name)
        .join(", ")
    ])
  );

const getBundleNames = templates =>
  Object.fromEntries(
    templates.map(template => [
      template.id,
      get(template, "templatesBundles", [])
        .map(rel => rel.bundle.name)
        .join(", ")
    ])
  );

const getLicenseBundleNames = templates =>
  Object.fromEntries(
    templates.map(template => [
      template.id,
      get(template, "templatesLicenseBundles", [])
        .map(rel => rel.licenseBundle.name)
        .join(", ")
    ])
  );

const TemplateRow = ({ template, filter, row, featureNames, bundleNames, licenseBundleNames }) => (
  <Grid.Row data-test={`templates-tr-${row}`}>
    <Grid.Column data-test="name" width={3}>
      {highLightedSearchMatch(filter.name, template.name)}
    </Grid.Column>
    <Grid.Column data-test="features" width={4}>
      {highLightedSearchMatch(filter.name, featureNames[template.id])}
    </Grid.Column>
    <Grid.Column data-test="bundles" width={4}>
      {highLightedSearchMatch(filter.name, bundleNames[template.id])}
    </Grid.Column>
    <Grid.Column data-test="license-bundles" width={3}>
      {highLightedSearchMatch(filter.name, licenseBundleNames[template.id])}
    </Grid.Column>
    <Grid.Column textAlign="center" width={2}>
      <AppLink
        data-test="templateDetailsLink"
        icon="edit"
        size="large"
        to={`/templates/${template.id}/edit`}
        content="Edit"
      />
      <DeleteModal
        data-test="templateDeleteBtn"
        recordSlug={template.id}
        recordName={template.name}
        resourceName="templates"
      />
    </Grid.Column>
  </Grid.Row>
);

export default TemplateList;
